import { NzTableFilterFn, NzTableFilterList, NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import { StatsValuesConfigInterface, SystemOverviewAntTableInterface } from './system-portfolio-overview-table-config';

export type StatusColumnKey = keyof Pick<
  SystemOverviewAntTableInterface,
  'performance' | 'safetyScore' | 'rte' | 'risk' | 'warrantyState'
>;

function statusText(status?: StatsValuesConfigInterface): string | null {
  return status?.label ?? status?.title ?? null;
}

function withUnknownPinnedLast<T, V>(
  read: (item: T) => V | null | undefined,
  compareKnown: (a: V, b: V) => number
): NzTableSortFn<T> {
  return (a: T, b: T, sortOrder?: NzTableSortOrder) => {
    const valueA = read(a);
    const valueB = read(b);

    if (valueA == null || valueB == null) {
      if (valueA == null && valueB == null) return 0;
      // nz-table negates the comparator on descend, so pre-negate to keep these last either way.
      const verdict = valueA == null ? 1 : -1;
      return sortOrder === 'descend' ? -verdict : verdict;
    }

    return compareKnown(valueA, valueB);
  };
}

export function sortByStatusSeverity(key: StatusColumnKey): NzTableSortFn<SystemOverviewAntTableInterface> {
  return withUnknownPinnedLast(
    (row: SystemOverviewAntTableInterface) => row[key]?.severity,
    (a, b) => b - a
  );
}

export function sortByNumber(
  accessor: (row: SystemOverviewAntTableInterface) => number | null | undefined
): NzTableSortFn<SystemOverviewAntTableInterface> {
  return withUnknownPinnedLast(accessor, (a, b) => a - b);
}

export function statusFilterOptions(rows: SystemOverviewAntTableInterface[], key: StatusColumnKey): NzTableFilterList {
  const texts = new Set<string>();
  rows.forEach((row) => {
    const text = statusText(row[key]);
    if (text) texts.add(text);
  });

  return Array.from(texts)
    .sort()
    .map((text) => ({ text, value: text }));
}

export function filterByStatusLabel(key: StatusColumnKey): NzTableFilterFn<SystemOverviewAntTableInterface> {
  return (value, row) => {
    const text = statusText(row[key]);
    return !!text && (value as string[]).includes(text);
  };
}
